import React, { useState } from "react";
import { Loader2, FileDown, Mail, FileText, CheckCircle2, Link2 } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { QUOTE_STATUS_META, QUOTE_STATUSES } from "../../lib/quoteConstants";
import { fmtEUR, isNoVat, vatMention } from "../../lib/invoiceConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import ReminderPanel from "./ReminderPanel";

const QUOTE_REMINDER_TYPES = [
  { value: "quote_not_viewed", label: "Devis non consulté" },
  { value: "quote_viewed_not_accepted", label: "Devis consulté non accepté" },
  { value: "quote_expiring", label: "Devis bientôt expiré" },
];

const QuoteStatusBadge = ({ status }) => {
  const m = QUOTE_STATUS_META[status] || { label: status, color: "bg-gray-100 text-gray-600" };
  return <Badge variant="outline" className={`${m.color} font-medium`} data-testid={`quote-status-${status}`}>{m.label}</Badge>;
};

const QuoteDetail = ({ open, onOpenChange, quote, role, onUpdated, onConverted }) => {
  const [statusValue, setStatusValue] = useState("");
  const [sendEmail, setSendEmail] = useState("");
  const [loading, setLoading] = useState(false);

  if (!quote) return null;
  const isAdmin = role === "admin";
  const noVat = isNoVat(quote.seller?.vatRegime);

  const downloadPdf = async () => {
    try {
      const res = await API.get(`/quotes/${quote.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error("Téléchargement impossible.");
    }
  };

  const doSend = async () => {
    setLoading(true);
    try {
      const { data } = await API.post(`/quotes/${quote.id}/send`, { email: sendEmail || null });
      toast.success("Devis envoyé par email");
      setSendEmail("");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyStatus = async () => {
    if (!statusValue) return;
    setLoading(true);
    try {
      const { data } = await API.patch(`/quotes/${quote.id}/status`, { status: statusValue });
      toast.success("Statut devis mis à jour");
      setStatusValue("");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const convert = async () => {
    setLoading(true);
    try {
      const { data } = await API.post(`/quotes/${quote.id}/convert`);
      toast.success("Devis converti en facture (brouillon)");
      onConverted?.(data.invoiceId);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshDoc = async () => {
    try {
      const { data } = await API.get(`/quotes/${quote.id}`);
      onUpdated?.(data);
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[hsl(207,78%,42%)]">{quote.number || "Brouillon"}</span>
            <QuoteStatusBadge status={quote.status} />
          </DialogTitle>
          <DialogDescription>Détail du devis, validation client, envoi et conversion en facture.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {quote.title && <p className="font-medium text-slate-800">{quote.title}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-slate-800 mb-1">Client</p>
              <p>{quote.buyer?.name}</p>
              {quote.buyer?.email && <p className="text-muted-foreground text-xs">{quote.buyer.email}</p>}
              {quote.buyer?.siren && <p className="text-muted-foreground text-xs">SIREN : {quote.buyer.siren}</p>}
            </div>
            <div className="text-xs">
              <p><span className="text-muted-foreground">Date :</span> {quote.issueDate}</p>
              {quote.validUntil && <p><span className="text-muted-foreground">Valable jusqu&apos;au :</span> {quote.validUntil}</p>}
              {quote.interventionDossier && <p><span className="text-muted-foreground">Dossier :</span> {quote.interventionDossier}</p>}
            </div>
          </div>

          <Separator />
          <div className="space-y-1">
            {quote.lines?.map((l, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>{l.designation} <span className="text-muted-foreground">×{l.quantity} @ {fmtEUR(l.unitPriceHT)}{!noVat ? ` (TVA ${l.vatRate}%)` : ""}</span></span>
                <span className="font-medium">{fmtEUR(l.totalHT)}</span>
              </div>
            ))}
          </div>
          <div className="bg-slate-50 rounded p-3 ml-auto sm:w-72 text-sm space-y-1">
            <div className="flex justify-between"><span>Total HT</span><span>{fmtEUR(quote.totalHT)}</span></div>
            {!noVat && quote.vatBreakdown?.map((b) => (<div key={b.rate} className="flex justify-between text-xs text-muted-foreground"><span>TVA {b.rate}%</span><span>{fmtEUR(b.vatAmount)}</span></div>))}
            <div className="flex justify-between font-bold text-[hsl(207,78%,42%)] border-t pt-1"><span>{noVat ? "Net à payer" : "Total TTC"}</span><span>{fmtEUR(noVat ? quote.totalHT : quote.totalTTC)}</span></div>
            {noVat && vatMention(quote.seller?.vatRegime) && <p className="text-[11px] text-muted-foreground pt-1">{vatMention(quote.seller?.vatRegime)}</p>}
          </div>

          {/* Validation client */}
          {quote.validation && (
            <div className="bg-emerald-50 rounded-lg p-3 text-xs">
              <p className="font-semibold flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Validation client</p>
              <p>{quote.validation.decision === "accepte" ? "Accepté" : "Refusé"} par <strong>{quote.validation.validatorName}</strong> ({quote.validation.validatorEmail})</p>
              <p className="text-muted-foreground">{(quote.validation.at || "").slice(0, 16).replace("T", " ")}</p>
              {quote.validation.comment && <p className="italic">« {quote.validation.comment} »</p>}
            </div>
          )}

          {/* Actions */}
          <Separator />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="quote-pdf-button"><FileDown className="w-4 h-4 mr-2" /> PDF</Button>
            {isAdmin && quote.publicToken && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const url = `${window.location.origin}/devis/${quote.publicToken}`;
                  navigator.clipboard?.writeText(url);
                  toast.success("Lien de validation copié");
                }}
                data-testid="quote-copy-link"
              >
                <Link2 className="w-4 h-4 mr-2" /> Copier le lien client
              </Button>
            )}
            {isAdmin && quote.status === "accepte" && !quote.invoiceId && (
              <Button size="sm" className="btn-primary" onClick={convert} disabled={loading} data-testid="quote-convert-button">
                <FileText className="w-4 h-4 mr-2" /> Convertir en facture
              </Button>
            )}
            {isAdmin && quote.invoiceId && (
              <span className="text-xs text-teal-700 self-center">✓ Facture générée</span>
            )}
          </div>
          {isAdmin && quote.clientDownloadedAt && (
            <p className="text-xs text-emerald-700">✓ Consulté par le client le {(quote.clientDownloadedAt || "").slice(0, 16).replace("T", " ")}</p>
          )}

          {isAdmin && (
            <>
              <Separator />
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <p className="font-semibold text-sm text-slate-800">Actions administrateur</p>
                <div className="space-y-2">
                  <Label className="text-xs">Envoyer par email</Label>
                  <div className="flex gap-2">
                    <Input value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder={quote.buyer?.email || "email@client.fr"} data-testid="quote-send-email-input" />
                    <Button onClick={doSend} disabled={loading} data-testid="quote-send-button"><Mail className="w-4 h-4 mr-2" /> Envoyer</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Changer le statut</Label>
                  <div className="flex gap-2">
                    <Select value={statusValue} onValueChange={setStatusValue}>
                      <SelectTrigger data-testid="quote-status-select"><SelectValue placeholder="Nouveau statut" /></SelectTrigger>
                      <SelectContent>{QUOTE_STATUSES.map((s) => (<SelectItem key={s} value={s}>{QUOTE_STATUS_META[s].label}</SelectItem>))}</SelectContent>
                    </Select>
                    <Button disabled={!statusValue || loading} onClick={applyStatus} data-testid="quote-apply-status">Appliquer</Button>
                  </div>
                </div>
              </div>
            </>
          )}

          {isAdmin && !["accepte", "refuse", "expire", "converti_en_facture"].includes(quote.status) && (
            <ReminderPanel docType="quote" doc={quote} types={QUOTE_REMINDER_TYPES} onSent={refreshDoc} />
          )}

          {/* Historique */}
          <Separator />
          <div>
            <p className="font-semibold text-sm text-slate-800 mb-2">Historique</p>
            <div className="space-y-1">
              {(quote.statusHistory || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                  <QuoteStatusBadge status={h.status} />
                  {h.note && <span className="text-slate-600">— {h.note}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDetail;
